<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { ref, computed } from 'vue'

import { saveLocale } from '@/utils/i18n'
import {
  LANGUAGE_OPTIONS,
  PRIMARY_LANGEAGE_OPTIONS,
} from '@/constants/enums/Language'
import ATooltip from '@/components/atoms/ATooltip/index.vue'
import ABaseIcon from '@/components/atoms/ABaseIcon/index.vue'
import LanguageToggleSvg from '@/assets/images/language-toggle.svg'
import { trackClickLangSwitch } from '@/utils/tracking/eventTracking'

const { locale, setLocale } = useI18n()
/**
 * Props
 */
type Props = {
  onlyPrimaryLocales?: boolean
  needsReload?: boolean
}
const props = withDefaults(defineProps<Props>(), {
  onlyPrimaryLocales: false,
  needsReload: true,
})

/**
 * Refs
 */
const isMenuToggleVisible = ref(false)

/**
 * Computed
 */
const items = computed(() => {
  return props.onlyPrimaryLocales ? PRIMARY_LANGEAGE_OPTIONS : LANGUAGE_OPTIONS
})

/**
 * Methods
 */
const changeLocale = (value: string) => {
  trackClickLangSwitch({ langCode: value })
  saveLocale(value)

  if (!props.needsReload) {
    setLocale(value)
    return
  }

  const url = new URL(location.href)
  const currentLocaleFromSearchParams = url.searchParams.get('locale')

  // 言語のクエリーパラメータが指定されている場合は上書きする
  if (!!currentLocaleFromSearchParams && currentLocaleFromSearchParams !== value) {
    url.searchParams.set('locale', value)
    location.href = url.href
  }
  else {
    location.reload()
  }
}
</script>

<template>
  <!-- 通知パネルよりも前面に出るようにz-indexを設定 -->
  <v-menu
    v-model="isMenuToggleVisible"
    :z-index="3001"
    transition="none"
  >
    <!-- eslint-disable-next-line -->
    <template #activator="{ props }">
      <div
        v-bind="props"
        class="language"
      >
        <!-- TODO: 型の修正 -->
        <ATooltip
          :tip="undefined as any"
          :z-index="3001"
          position="bottom-left"
          open-on-hover
          activator-class="language__activator"
        >
          <template #activator>
            <div
              class="language__menu-toggle"
              :class="{ 'language__menu-toggle__hover': isMenuToggleVisible }"
            >
              <ABaseIcon
                :src="LanguageToggleSvg"
                size="24px"
              />
            </div>
          </template>
          <template #balloon>
            <p
              v-if="!isMenuToggleVisible"
              class="language__hover-text"
            >
              {{ $t("common.switch.current_language") }}
            </p>
          </template>
        </ATooltip>
      </div>
    </template>
    <v-card rounded="lg">
      <v-list>
        <v-list-item
          v-for="item in items"
          :key="item.title"
          :title="item.title"
          class="list-item"
          :class="{ 'is-current': locale === item.value }"
          @click="() => changeLocale(item.value)"
        />
      </v-list>
    </v-card>
  </v-menu>
</template>

<style lang="scss" scoped>
.language {
  padding-right: 3px;
  padding-left: 8px;
  cursor: pointer;
}

:deep(.language__activator) {
  display: flex;
  align-items: center;
  justify-content: center;
}

.language__menu-toggle {
  display: flex;
  align-items:     center;
  justify-content: center;
  width:   30px;
  height:  30px;

  &:hover {
    background: rgb(var(--v-theme-primary-lighten-2));
    border-radius: 2px;
  }
}

.language__menu-toggle__hover {
  background: rgb(var(--v-theme-primary-lighten-2));
  border-radius: 2px;
}

.language__hover-text {
  padding: 6px 10px;
  color: rgb(var(--v-theme-ui-text-black));
  white-space: nowrap;
  background-color: rgb(var(--v-theme-ui-white));
  border-radius: 4px;
  box-shadow: 0 0 8px rgba(var(--v-theme-ui-black), 16%);
}

.v-card {
  width: 80px;
  margin: 4px auto;
}

.v-list-item {
  color: rgb(var(--v-theme-ui-text-black));
  cursor: pointer;

  &.v-list-item--density-default {
    min-height: 0;
  }

  &.list-item {
    padding: 4px 8px;
    text-align: center;
  }

  &.is-current {
    color: rgb(var(--v-theme-ui-graph-blue-2));
  }
}
</style>
